import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import axios from "axios";

import { Container, Row, Col, Form, Button, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { toast } from "react-toastify";
import { CHAT_API } from "../../../api/api";

import { FaSearch } from "react-icons/fa";
import { IoSend } from "react-icons/io5";
import { HiPencil } from "react-icons/hi";
import { RxCross2 } from "react-icons/rx";
import { CgAttachment } from "react-icons/cg";

import albumImage from "../../../assets/icons/student/album.svg";
import PLACEHOLDER_IMG from "../../../assets/student/place-holder.png";

import { initialsFromName } from "../../../utils/getInitials";
import { handleApiError } from "../../../utils/apiErrorHandler";

import "./StudentChats.css";

const StudentChats = () => {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("all");

  const [tutors, setTutors] = useState([]);
  const [selectedTutor, setSelectedTutor] = useState(null);

  const [messages, setMessages] = useState([]);
  const [pauseChat, setPauseChat] = useState(false);
  const [query, setQuery] = useState(null);
  const [messageText, setMessageText] = useState("");

  const [selectedFile, setSelectedFile] = useState(null);
  const [editingMessageId, setEditingMessageId] = useState(null);

  // Chat Resolved prompt
  const [showResolvedPrompt, setShowResolvedPrompt] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");

  //loading
  const [loadingAllChats, setloadingAllChats] = useState(true);
  const [loadingSendMsg, setloadingSendMsg] = useState(false);
  const [loadingClosingQuery, setLoadingClosingQuery] = useState(false);

  const fileInputRef = useRef();
  const messagesEndRef = useRef();
  const currentUserRole = "student";

  // main toast error
  const showErrorToast = (message) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 1500,
      theme: "colored",
    });
  };

  const handleAttach = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileData = {
      file, // <-- raw File object from input
      preview: URL.createObjectURL(file),
      type: file.type,
      name: file.name,
    };

    setSelectedFile(fileData);
    // Clear text input if a file is selected
    setMessageText("");
  };

  // Cleanup object URL (memory leak fix)
  useEffect(() => {
    return () => {
      if (selectedFile?.preview?.startsWith("blob:")) {
        URL.revokeObjectURL(selectedFile.preview);
      }
    };
  }, [selectedFile]);

  const fetchTutors = useCallback(async () => {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("user_id");

    if (!userId || !token) {
      setloadingAllChats(false);
      navigate("/signin");

      return;
    }

    try {
      const response = await axios.post(
        CHAT_API.GET_CHAT_LIST,
        { user_id: userId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = response.data;

      if (data.statusCode === 200) {
        setTutors(data.chatList || []);
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Error fetching tutors:", error);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(error, navigate, showErrorToast, "/signin");
    } finally {
      setloadingAllChats(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchTutors();
  }, [fetchTutors]);

  // Click tutor → fetch messages
  const handleTutorClick = async (tutor) => {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("user_id");

    if (!userId || !token) {
      navigate("/signin");
      return;
    }

    setShowResolvedPrompt(false); // reset prompt when switching chat
    setSelectedTutor(tutor);

    // Clear input & file when switching tutor
    setMessageText("");
    setSelectedFile(null);
    setEditingMessageId(null);
    if (fileInputRef.current) fileInputRef.current.value = "";

    if (!tutor.chat_room_id) {
      // No chat room yet → empty messages
      setMessages([]);
      setPauseChat(false);
      setQuery(null);
      return;
    }

    try {
      const response = await axios.post(
        CHAT_API.GET_MESSAGES,
        { user_id: userId, chat_room_id: tutor.chat_room_id },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const data = response.data;

      if (data.statusCode === 200) {
        const getFileType = (fileName = "") => {
          const ext = fileName.split(".").pop()?.toLowerCase();

          if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) {
            return "image";
          }

          return "file";
        };

        const formattedMessages = (data.messages || []).map((msg) => ({
          chatId: msg.chat_id,
          fromRole: msg.role?.trim().toLowerCase(),
          text: msg.message,
          time: msg.to_char,
          edited: msg.edited,
          file: msg.file_url
            ? {
                preview: msg.file_url,
                name: msg.file
                  ? msg.file.split("/").pop().replace(/^\d+_/, "")
                  : "File",
                type: getFileType(msg.file),
              }
            : null,
        }));

        setMessages(formattedMessages);
        setPauseChat(Boolean(data.pause_chat)); // true or false from API
        setQuery(data.query);

        await fetchTutors();
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Msg error:", error);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(error, navigate, showErrorToast, "/signin");
    }
  };

  // Scroll to bottom on messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Send message
  const handleStudentSendMsg = async () => {
    if (!selectedTutor) {
      showErrorToast("Please select a conversation first.");
      return;
    }

    if (!messageText.trim() && !selectedFile) return;

    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("user_id");

    if (!userId || !token) {
      setloadingSendMsg(false);
      navigate("/signin");

      return;
    }

    const formData = new FormData();
    formData.append("user_id", userId);
    formData.append("course_id", selectedTutor.course_id);
    formData.append("message", messageText.trim());

    if (selectedFile?.file) {
      formData.append("file_url", selectedFile.file);
    }

    setloadingSendMsg(true);
    try {
      const response = await axios.post(CHAT_API.SEND_MESSAGE, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = response.data;

      if (data.statusCode === 200) {
        // If chat_room_id was null → refresh page
        if (!selectedTutor.chat_room_id) {
          window.location.reload();
          return;
        }

        await handleTutorClick(selectedTutor);

        setMessageText("");
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Error send message:", error);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(error, navigate, showErrorToast, "/signin");
    } finally {
      setloadingSendMsg(false);
    }
  };

  const handleShowEditMsg = (msg) => {
    setMessageText(msg.text || "");
    setSelectedFile(msg.file || null);
    setEditingMessageId(msg.chatId);

    setTimeout(() => {
      document.querySelector(".student-doubts-input")?.focus();
    }, 0);
  };

  // Edit message
  const handleUpdateStudentSendMsg = async () => {
    if (!editingMessageId) {
      showErrorToast("No message selected to edit.");
      return;
    }

    const token = localStorage.getItem("token");

    if (!token) {
      setloadingSendMsg(false);
      navigate("/signin");

      return;
    }
    const formData = new FormData();
    formData.append("chat_id", editingMessageId);
    formData.append("message", messageText.trim());

    if (selectedFile?.file) {
      formData.append("file_url", selectedFile.file); // key matches backend
    }

    setloadingSendMsg(true);
    try {
      const response = await axios.post(CHAT_API.UPDATE_MESSAGE, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = response.data;

      if (data.statusCode === 200) {
        await handleTutorClick(selectedTutor);

        setEditingMessageId(null);
        setMessageText("");
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Error update message:", error);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(error, navigate, showErrorToast, "/signin");
    } finally {
      setloadingSendMsg(false);
    }
  };

  // CHAT RESOLVED PROMPT TIMER
  useEffect(() => {
    if (!selectedTutor || !query) return;
    if (pauseChat || query.is_closed || query.query_status === "resolved")
      return;

    const interval = setInterval(() => {
      setShowResolvedPrompt(true); // show prompt every 20 sec
    }, 20000);

    return () => clearInterval(interval);
  }, [selectedTutor, query, pauseChat]);

  const handleCloseQuery = async () => {
    if (!query?.query_id) return;

    const token = localStorage.getItem("token");

    if (!token) {
      setLoadingClosingQuery(false);
      navigate("/signin");

      return;
    }

    setLoadingClosingQuery(true);
    try {
      const response = await axios.post(
        CHAT_API.CLOSE_QUERY,
        { query_id: query.query_id },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = response.data;
      if (data.statusCode === 200) {
        setQuery((prev) => ({
          ...prev,
          is_closed: true,
          query_status: "resolved",
        }));
        setShowResolvedPrompt(false);
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error(error);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(error, navigate, showErrorToast, "/signin");
    } finally {
      setLoadingClosingQuery(false);
    }
  };

  // count
  const allCount = tutors.length;
  const unreadCount = tutors.filter((t) => Number(t.unseen_count) > 0).length;
  const readCount = tutors.filter((t) => Number(t.unseen_count) === 0).length;

  const normalizedSearch = searchTerm.trim().toLowerCase();

  const filteredTutors = useMemo(() => {
    let result = tutors;

    // Search filter
    if (normalizedSearch) {
      result = result.filter((t) =>
        (t.tutor_name || "").toLowerCase().includes(normalizedSearch),
      );
    }

    // Tab filter
    if (activeTab === "unread") {
      result = result.filter((t) => Number(t.unseen_count) > 0);
    }

    if (activeTab === "read") {
      result = result.filter((t) => Number(t.unseen_count) === 0);
    }

    return result;
  }, [tutors, normalizedSearch, activeTab]);

  if (loadingAllChats) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <Container fluid className="student-doubts-root-section">
      <Row className="mb-3">
        <h2 className="student-certificate-section-title">Chat Conversation</h2>
        <p className="student-certificate-section-description">
          Stay connected with mentors throughout your learning journey. 
        </p>
      </Row>

      <div className="student-doubts-root-section-divssss">
        <Row className="student-doubts-totalboxss">
          <Col md={4} className="student-doubts-left">
            <div className="student-doubts-tabs-wrapper mb-3">
              <button
                className={`student-doubts-tab ${activeTab === "all" ? "active" : ""}`}
                onClick={() => setActiveTab("all")}
              >
                All ({allCount})
              </button>

              <button
                className={`student-doubts-tab ${activeTab === "unread" ? "active" : ""}`}
                onClick={() => setActiveTab("unread")}
              >
                Unread ({unreadCount})
              </button>

              <button
                className={`student-doubts-tab ${activeTab === "read" ? "active" : ""}`}
                onClick={() => setActiveTab("read")}
              >
                Read ({readCount})
              </button>
            </div>

            <div className="student-doubts-search-wrap mb-3">
              <input
                type="text"
                placeholder="Search conversation"
                className="student-doubts-search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />

              <span className="student-doubts-search-icon">
                <FaSearch />
              </span>
            </div>

            <div className="student-doubts-list">
              {filteredTutors.length ? (
                filteredTutors.map((t) => (
                  <div
                    key={`${t.tutor_id}-${t.course_id}`}
                    className={`student-doubts-list-item ${
                      selectedTutor?.tutor_id === t.tutor_id &&
                      selectedTutor?.course_id === t.course_id
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => handleTutorClick(t)}
                  >
                    <div className="student-doubts-initials">
                      {initialsFromName(t.tutor_name)}
                    </div>

                    <div className="student-doubts-meta">
                      <div className="student-doubts-meta-top">
                        <span
                          className="student-doubts-name"
                          title={t.tutor_name}
                        >
                          {t.tutor_name}
                        </span>
                        {/* <span className="student-doubts-time">2</span> */}
                      </div>

                      <div
                        className="student-doubts-snippet"
                        // title={t.last_message}
                      >
                        {t.last_message}
                      </div>
                    </div>

                    {Number(t.unseen_count) > 0 && (
                      <div className="student-doubts-unread">
                        {t.unseen_count}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="tutor-student-doubts-empty text-center p-4 mt-5">
                  <img
                    src={PLACEHOLDER_IMG}
                    alt="no queries"
                    className="tutor-student-doubts-img-fluidsss"
                  />
                  <p className="mt-3 tutor-student-doubts-img-bottom-text">
                    No queries found
                  </p>
                </div>
              )}
            </div>
          </Col>

          <Col md={8} className="student-doubts-right p-0">
            {selectedTutor ? (
              <>
                <div className="student-doubts-right-header">
                  <div className="d-flex align-items-center gap-3">
                    <div className="student-doubts-header-initials">
                      {initialsFromName(selectedTutor.tutor_name)}
                    </div>

                    <div>
                      <div className="student-doubts-header-name">
                        {selectedTutor.tutor_name}
                      </div>
                      <div className="student-doubts-header-role">
                        {selectedTutor.course_title}
                      </div>
                    </div>
                  </div>
                </div>

                {/* CHAT AREA */}
                <div className="student-doubts-chat-area">
                  {messages?.map((m) => (
                    <div
                      key={m.chatId}
                      className={`d-flex flex-column mb-3 ${
                        m.fromRole === currentUserRole
                          ? "align-items-end"
                          : "align-items-start"
                      }`}
                    >
                      {m.edited && (
                        <div className="student-doubts-edited-label">
                          Edited
                        </div>
                      )}
                      <div className={`student-doubts-msg ${m.fromRole}`}>
                        <div className="student-doubts-msg-text">
                          {m.text && <div>{m.text}</div>}

                          {m.file && (
                            <>
                              {m.file?.type === "image" ? (
                                <a
                                  href={m.file.preview}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <img
                                    src={m.file.preview}
                                    alt="sent-img"
                                    style={{
                                      maxWidth: "200px",
                                      borderRadius: "8px",
                                      marginTop: "5px",
                                    }}
                                  />
                                </a>
                              ) : (
                                <div className="file-box">
                                  <a
                                    href={m.file.preview}
                                    download={m.file.name}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    <CgAttachment /> {m.file.name}
                                  </a>
                                </div>
                              )}
                            </>
                          )}
                        </div>

                        <div className="student-doubts-msg-footer">
                          <span className="msg-role-label">
                            {m.fromRole === "student"
                              ? "Student"
                              : m.fromRole === "tutor"
                                ? "By Tutor"
                                : "By Admin"}
                          </span>
                          <div className="student-doubts-msg-footer-two">
                            <span className="student-doubts-msg-time">
                              {m.time}
                            </span>

                            {m.fromRole === "student" && (
                              <HiPencil
                                className="msg-icon"
                                onClick={() => handleShowEditMsg(m)}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {/* CHAT RESOLVED PROMPT */}
                {showResolvedPrompt &&
                  !pauseChat &&
                  query?.is_closed === false && (
                    <div className="student-chat-resolved-prompt">
                      <span>Was this chat resolved?</span>
                      <div className="student-chat-resolved-actions">
                        <button
                          className="btn-yes"
                          disabled={loadingClosingQuery}
                          onClick={handleCloseQuery}
                        >
                          Yes
                        </button>
                        <button
                          className="btn-no"
                          onClick={() => {
                            setShowResolvedPrompt(false);
                          }}
                        >
                          No
                        </button>
                      </div>
                    </div>
                  )}

                {/* INPUT BAR */}
                {pauseChat ? (
                  <div className="admin-chat-paused-box">
                    ⚠ Chat has been paused by admin. Messaging is disabled.
                  </div>
                ) : (
                  <div className="student-doubts-input-bar">
                    <div className="student-doubts-input-inner">
                      {/* Show file preview inline before input text */}
                      {selectedFile && (
                        <div className="inline-file-preview">
                          <span className="file-name" title={selectedFile.name}>
                            <CgAttachment /> {selectedFile.name}
                          </span>

                          <span
                            className="remove-file"
                            onClick={() => {
                              setSelectedFile(null);
                              setMessageText("");
                              if (fileInputRef.current) {
                                fileInputRef.current.value = "";
                              }
                            }}
                            title="Remove file"
                          >
                            <RxCross2 />
                          </span>
                        </div>
                      )}

                      <Form.Control
                        className="student-doubts-input"
                        placeholder={
                          editingMessageId
                            ? "Edit your message..."
                            : selectedFile
                              ? "File selected — cannot type message"
                              : "Type a message..."
                        }
                        value={messageText}
                        onChange={(e) => setMessageText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            editingMessageId
                              ? handleUpdateStudentSendMsg()
                              : handleStudentSendMsg();
                          }
                        }}
                        autoComplete="off"
                        disabled={!!selectedFile}
                      />

                      {/* Attach icon */}
                      <img
                        src={albumImage}
                        alt="attach"
                        className="student-doubts-attach-icon"
                        onClick={() => fileInputRef.current.click()}
                      />

                      <input
                        ref={fileInputRef}
                        type="file"
                        hidden
                        onChange={handleAttach}
                      />
                    </div>

                    <Button
                      className="student-doubts-send-btn"
                      onClick={() =>
                        editingMessageId
                          ? handleUpdateStudentSendMsg()
                          : handleStudentSendMsg()
                      }
                      disabled={loadingSendMsg}
                    >
                      {loadingSendMsg ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <IoSend />
                      )}
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="tutor-student-doubts-empty text-center p-4 mt-5">
                <img
                  src={PLACEHOLDER_IMG}
                  alt="no queries"
                  className="tutor-student-doubts-img-fluidsss"
                />
                <p className="mt-3 tutor-student-doubts-img-bottom-text">
                  No queries found
                </p>
                <p className="mt-0 tutor-student-doubts-img-bottom-para">
                  Select a conversation to connect with your mentor.
                </p>
              </div>
            )}
          </Col>
        </Row>
      </div>
    </Container>
  );
};

export default StudentChats;
